import { ContainerSpec, GpuResource, HealthCheckSpec, Workload } from "../../../../schema/types/workload/entity";
import {
  getDefaultContainerInput,
  getDefaultGpuInput,
  getDefaultOptionsInputBase,
  getDefaultProbeInput,
  getDefaultWorkloadInput,
} from "./utils";
import { v4 as uuidv4 } from "uuid";
import { volumeToInput } from "./volume";
import { linksOf } from "../../../../services/cpln";
import { parseLink } from "../../../../utils/linkParser/linkParser";
import {
  ContainerInput,
  GpuInput,
  LocalOptionsInput,
  ProbeInput,
  WorkloadInput,
} from "../../../../schema/types/workload/input";
import { cpuToParts, memoryToParts } from "../../../volumeset/utils";
import { envToInput } from "./envVar";
import jsYaml from "js-yaml";
import { tagsToInput } from "../../../../mst/kinds/utils";
import { defaultValues } from "../../../../services/defaults";
import { sidecarToInput } from "../../../../mst/stores/cpln.jwt.client.draft";
import { getDefaultKindSelectState } from "../../../../components/antd/kindselect/KindSelect";

function probeToInput(p?: HealthCheckSpec): ProbeInput {
  const i = getDefaultProbeInput();

  if (!p) {
    return i;
  }

  i.alreadySet = true;
  if (p.failureThreshold !== undefined) {
    i.failureThreshold = String(p.failureThreshold);
  }
  if (p.successThreshold !== undefined) {
    i.successThreshold = String(p.successThreshold);
  }
  if (p.initialDelaySeconds !== undefined) {
    i.initialDelaySeconds = String(p.initialDelaySeconds);
  }
  if (p.periodSeconds !== undefined) {
    i.periodSeconds = String(p.periodSeconds);
  }
  if (p.timeoutSeconds !== undefined) {
    i.timeoutSeconds = String(p.timeoutSeconds);
  }

  if (p.exec?.command && p.exec.command.length > 0) {
    i.type = "exec";
    const cmd = p.exec.command;
    i.exec.command = cmd[0];
    i.exec.args = cmd.slice(1).map((v) => ({ id: uuidv4(), value: v }));
    return i;
  }

  if (p.grpc) {
    i.type = "grpc";
    if (p.grpc.port !== undefined) {
      i.grpc.port = String(p.grpc.port);
    }
    return i;
  }

  if (p.httpGet) {
    i.type = "http";
    i.httpGet.path = p.httpGet.path;
    i.httpGet.scheme = p.httpGet.scheme;
    if (p.httpGet.port !== undefined) {
      i.httpGet.port = String(p.httpGet.port);
    }
    i.httpGet.httpHeaders = (p.httpGet.httpHeaders || []).map((h) => ({ id: uuidv4(), name: h.name, value: h.value }));
    return i;
  }

  if (p.tcpSocket) {
    i.type = "tcp";
    if (p.tcpSocket.port !== undefined) {
      i.tcpSocket.port = String(p.tcpSocket.port);
    }
  }

  return i;
}

function gpuToInput(gpu?: GpuResource): GpuInput {
  const g = getDefaultGpuInput();

  if (!gpu) {
    return g;
  }

  if (gpu.nvidia) {
    g.type = "nvidia";
    g.nvidia.model = gpu.nvidia.model;
    g.nvidia.quantity = String(gpu.nvidia.quantity);
    return g;
  }

  if (gpu.custom) {
    g.type = "custom";
    // Reset to empty string to avoid default "1" leaking for unset quantity
    g.custom.quantity = "";
    g.custom.resource = gpu.custom.resource;
    if (gpu.custom.quantity) {
      g.custom.quantity = String(gpu.custom.quantity);
    }
    if (gpu.custom.runtimeClass) {
      g.custom.runtimeClass = gpu.custom.runtimeClass;
    }
    return g;
  }

  return g;
}

function containerToInput(c: ContainerSpec): ContainerInput {
  const id = uuidv4();
  const i: ContainerInput = {
    ...getDefaultContainerInput(),
    id: id,
    name: {
      isManual: true,
      value: c.name,
    },
    cpu: cpuToParts(c.cpu),
    memory: memoryToParts(c.memory),
    env: envToInput(c.env || []),
    volumes: volumeToInput(c.volumes || []),
    gpu: gpuToInput(c.gpu),
    inheritEnv: c.inheritEnv,
    livenessProbe: probeToInput(c.livenessProbe),
    readinessProbe: probeToInput(c.readinessProbe),
  };

  const parsedImage = parseLink(c.image, { useInputCtx: true });
  if (parsedImage.isValid) {
    i.image = {
      isExternal: false,
      cplnValue: {
        value: parsedImage.name,
        link: parsedImage.absolute,
      },
      externalValue: "",
      cplnFetchAttempted: false,
      isFromSavedWorkload: true,
    };
  } else {
    i.image = {
      isExternal: true,
      cplnValue: getDefaultKindSelectState(),
      externalValue: c.image,
      cplnFetchAttempted: false,
      isFromSavedWorkload: true,
    };
  }

  if (c.workingDir) {
    i.workingDir = c.workingDir;
  }
  if (c.command) {
    i.command = c.command;
  }
  if (c.args) {
    i.args = c.args.map((v) => ({ id: uuidv4(), value: v }));
  }

  if (c.minCpu) {
    i.minCpu = cpuToParts(c.minCpu);
  }
  if (c.minMemory) {
    i.minMemory = memoryToParts(c.minMemory);
  }

  if (c.lifecycle?.preStop?.exec?.command && c.lifecycle.preStop.exec.command.length > 0) {
    const cmd = c.lifecycle.preStop.exec.command;
    i.lifecycle.preStop.exec = {
      isConfigured: true,
      command: cmd[0],
      args: cmd.slice(1).map((v) => ({ id: uuidv4(), value: v })),
    };
  }
  if (c.lifecycle?.postStart?.exec?.command && c.lifecycle.postStart.exec.command.length > 0) {
    const cmd = c.lifecycle.postStart.exec.command;
    i.lifecycle.postStart.exec = {
      isConfigured: true,
      command: cmd[0],
      args: cmd.slice(1).map((v) => ({ id: uuidv4(), value: v })),
    };
  }

  if (c.metrics) {
    i.metrics = {
      isEnabled: true,
      path: c.metrics.path,
      port: String(c.metrics.port),
      dropMetrics: (c.metrics.dropMetrics || []).map((dm) => ({ id: uuidv4(), value: dm })),
    };
  }

  if (c.port && !c.ports) {
    i.ports = [{ id: uuidv4(), protocol: "http", number: String(c.port) }];
  } else {
    i.ports = (c.ports || []).map((p) => ({ id: uuidv4(), protocol: p.protocol, number: String(p.number) }));
  }

  // Serves traffic is handled at workload level

  return i;
}

export function workloadToInput(workload?: Workload): WorkloadInput {
  if (!workload) {
    return getDefaultWorkloadInput();
  }

  const df = defaultValues.workload;

  const type = workload.spec.type;
  const containers: ContainerInput[] = workload.spec.containers.map(containerToInput);
  for (const cIndexStr in containers) {
    const cIndex = Number(cIndexStr);
    const container = containers[cIndex];
    container.activeTabKey = `container-${cIndex}-image`;
  }
  // handle serves traffic
  if (type === "serverless") {
    for (const container of containers) {
      if (container.ports.length > 0) {
        container.servesTraffic = true;
        break;
      }
    }
  }

  const selfLink = linksOf(workload).self!;
  const { org, gvc } = parseLink(selfLink, { useInputCtx: true });

  const input: WorkloadInput = {
    ...getDefaultWorkloadInput(),
    id: workload.id,
    version: workload.version,
    name: workload.name,
    description: workload.description,
    gvc: {
      value: gvc,
      link: `/org/${org}/gvc/${gvc}`,
    },
    type: type,
    containers: containers,
    tags: tagsToInput(workload.tags),
    supportDynamicTags: workload.spec.supportDynamicTags,
    meta: {
      activeKeyContainer: `container-${containers[0].id}`,
      activeKeyOption: "defaultOptions",
      isCreate: false,
      secretCache: {},
      workloadLink: selfLink,
      initialImageFetchIsDone: false,
      showExampleAlert: false,
    },
  };

  if (workload.spec.extras) {
    input.extras = jsYaml.dump(workload.spec.extras);
  }

  if (workload.spec.identityLink) {
    const parsedIdentity = parseLink(workload.spec.identityLink, { useInputCtx: true });
    input.identity = {
      fixError: "",
      identityName: "",
      isFixing: false,
      isValid: false,
      isValidating: true, // Start as validating to show spinner until async validation completes
      invalidError: "",
      policyName: "",
      value: {
        value: parsedIdentity.name,
        link: parsedIdentity.absolute,
      },
    };
  }

  if (workload.spec.defaultOptions) {
    const o = workload.spec.defaultOptions;
    input.defaultOptions = {
      ...getDefaultOptionsInputBase(),
      alreadySet: true,
      override: false,
      capacityAI: o.capacityAI || false,
      capacityAIUpdateMinutes: String(o.capacityAIUpdateMinutes ?? ""),
      debug: o.debug || false,
      multiZone: {
        isEnabled: o.multiZone?.enabled || false,
      },
      suspend: o.suspend || false,
      timeoutSeconds: String(o.timeoutSeconds ?? ""),
    };

    if (workload.spec.defaultOptions.autoscaling?.minScale !== undefined) {
      input.defaultOptions.autoscaling.minScale = String(workload.spec.defaultOptions.autoscaling.minScale);
    }
    if (workload.spec.defaultOptions.autoscaling?.maxScale !== undefined) {
      input.defaultOptions.autoscaling.maxScale = String(workload.spec.defaultOptions.autoscaling.maxScale);
    }
    if (workload.spec.defaultOptions.autoscaling?.scaleToZeroDelay !== undefined) {
      input.defaultOptions.autoscaling.scaleToZeroDelay = String(
        workload.spec.defaultOptions.autoscaling.scaleToZeroDelay,
      );
    }
    if (workload.spec.defaultOptions.autoscaling?.maxConcurrency !== undefined) {
      input.defaultOptions.autoscaling.maxConcurrency = String(workload.spec.defaultOptions.autoscaling.maxConcurrency);
    }
    if (workload.spec.defaultOptions.autoscaling?.metricPercentile) {
      input.defaultOptions.autoscaling.metricPercentile = workload.spec.defaultOptions.autoscaling.metricPercentile;
    }
    if (workload.spec.defaultOptions.autoscaling?.target !== undefined) {
      input.defaultOptions.autoscaling.target = String(workload.spec.defaultOptions.autoscaling.target);
    }

    if ((workload.spec.defaultOptions.autoscaling?.multi || []).length > 0) {
      input.defaultOptions.autoscaling.metric = "multi";
      // Reset to empty strings before populating from workload data
      // to avoid default values being added for metrics that don't exist
      input.defaultOptions.autoscaling.multi = { cpu: "", memory: "", rps: "" };
      for (const multiItem of workload.spec.defaultOptions.autoscaling!.multi || []) {
        if (multiItem.metric === "cpu") {
          input.defaultOptions.autoscaling.multi.cpu = String(multiItem.target);
        }
        if (multiItem.metric === "memory") {
          input.defaultOptions.autoscaling.multi.memory = String(multiItem.target);
        }
        if (multiItem.metric === "rps") {
          input.defaultOptions.autoscaling.multi.rps = String(multiItem.target);
        }
      }
    } else {
      input.defaultOptions.autoscaling.metric = workload.spec.defaultOptions.autoscaling?.metric || "disabled";
      if (workload.spec.defaultOptions.autoscaling?.keda) {
        input.defaultOptions.autoscaling.keda = jsYaml.dump(workload.spec.defaultOptions.autoscaling.keda);
      }
    }
  }

  for (const lo of workload.spec.localOptions || []) {
    const parsedLocation = parseLink(lo.location, { useInputCtx: true });
    const id = uuidv4();
    const i: LocalOptionsInput = {
      ...getDefaultOptionsInputBase(),
      id: id,
      location: parsedLocation.name,
      // Below is same as default options
      capacityAI: lo.capacityAI || false,
      capacityAIUpdateMinutes: String(lo.capacityAIUpdateMinutes ?? ""),
      debug: lo.debug || false,
      multiZone: {
        isEnabled: lo.multiZone?.enabled || false,
      },
      suspend: lo.suspend || false,
      timeoutSeconds: String(lo.timeoutSeconds ?? ""),
    };

    // Autoscaling for local options
    if (lo.autoscaling?.minScale !== undefined) {
      i.autoscaling.minScale = String(lo.autoscaling.minScale);
    }
    if (lo.autoscaling?.maxScale !== undefined) {
      i.autoscaling.maxScale = String(lo.autoscaling.maxScale);
    }
    if (lo.autoscaling?.scaleToZeroDelay !== undefined) {
      i.autoscaling.scaleToZeroDelay = String(lo.autoscaling.scaleToZeroDelay);
    }
    if (lo.autoscaling?.maxConcurrency !== undefined) {
      i.autoscaling.maxConcurrency = String(lo.autoscaling.maxConcurrency);
    }
    if (lo.autoscaling?.metricPercentile) {
      i.autoscaling.metricPercentile = lo.autoscaling.metricPercentile;
    }
    if (lo.autoscaling?.target !== undefined) {
      i.autoscaling.target = String(lo.autoscaling.target);
    }

    if ((lo.autoscaling?.multi || []).length > 0) {
      i.autoscaling.metric = "multi";
      // Reset to empty strings before populating from workload data
      // to avoid default values being added for metrics that don't exist
      i.autoscaling.multi = { cpu: "", memory: "", rps: "" };
      for (const multiItem of lo.autoscaling!.multi!) {
        if (multiItem.metric === "cpu") {
          i.autoscaling.multi.cpu = String(multiItem.target);
        }
        if (multiItem.metric === "memory") {
          i.autoscaling.multi.memory = String(multiItem.target);
        }
        if (multiItem.metric === "rps") {
          i.autoscaling.multi.rps = String(multiItem.target);
        }
      }
    } else {
      i.autoscaling.metric = lo.autoscaling?.metric || "disabled";
      if (lo.autoscaling?.keda) {
        i.autoscaling.keda = jsYaml.dump(lo.autoscaling.keda);
      }
    }

    input.localOptions.push(i);
  }

  input.securityOptions = {
    filesystemGroupId: String(workload.spec.securityOptions?.filesystemGroupId ?? ""),
    runAsUser: String(workload.spec.securityOptions?.runAsUser ?? ""),
  };

  if (workload.spec.rolloutOptions) {
    input.rolloutOptions = {
      isEnabled: true,
      maxSurgeReplicas: String(workload.spec.rolloutOptions.maxSurgeReplicas || ""),
      minReadySeconds: String(workload.spec.rolloutOptions.minReadySeconds),
      scalingPolicy: workload.spec.rolloutOptions.scalingPolicy,
      terminationGracePeriodSeconds: String(workload.spec.rolloutOptions.terminationGracePeriodSeconds),
      maxUnavailableReplicas: workload.spec.rolloutOptions.maxUnavailableReplicas || "",
    };
  }

  if (workload.spec.requestRetryPolicy) {
    input.requestRetryPolicy = {
      attempts: String(workload.spec.requestRetryPolicy.attempts),
      retryOn: workload.spec.requestRetryPolicy.retryOn,
    };
  }

  if (workload.spec.job) {
    input.job = {
      schedule: workload.spec.job.schedule || df.job.schedule.default,
      activeDeadlineSeconds: String(workload.spec.job.activeDeadlineSeconds ?? ""),
      concurrencyPolicy: workload.spec.job.concurrencyPolicy,
      historyLimit: String(workload.spec.job.historyLimit),
      restartPolicy: workload.spec.job.restartPolicy || df.job.restartPolicy.default,
    };
  }

  input.loadBalancer.replicaDirect = workload.spec.loadBalancer?.replicaDirect || false;
  if (workload.spec.loadBalancer?.direct?.enabled) {
    input.loadBalancer.direct.isEnabled = true;
    input.loadBalancer.direct.ports = (workload.spec.loadBalancer.direct.ports || []).map((p) => ({
      id: uuidv4(),
      scheme: p.scheme || "",
      externalPort: String(p.externalPort),
      containerPort: String(p.containerPort || ""),
      protocol: p.protocol,
    }));
    if (workload.spec.loadBalancer.direct.ipSet) {
      const parsedIpSet = parseLink(workload.spec.loadBalancer.direct.ipSet, { useInputCtx: true });
      input.loadBalancer.direct.ipSet = {
        value: parsedIpSet.name,
        link: parsedIpSet.absolute,
      };
    }
  }

  if (workload.spec.loadBalancer?.geoLocation?.enabled) {
    input.loadBalancer.geoLocation.isEnabled = true;
    if (workload.spec.loadBalancer?.geoLocation.headers?.asn) {
      input.loadBalancer.geoLocation.headers.asn = workload.spec.loadBalancer?.geoLocation.headers.asn;
    }
    if (workload.spec.loadBalancer?.geoLocation.headers?.city) {
      input.loadBalancer.geoLocation.headers.city = workload.spec.loadBalancer?.geoLocation.headers.city;
    }
    if (workload.spec.loadBalancer?.geoLocation.headers?.country) {
      input.loadBalancer.geoLocation.headers.country = workload.spec.loadBalancer?.geoLocation.headers.country;
    }
    if (workload.spec.loadBalancer?.geoLocation.headers?.region) {
      input.loadBalancer.geoLocation.headers.region = workload.spec.loadBalancer?.geoLocation.headers.region;
    }
  }

  const external = workload.spec.firewallConfig?.external;
  const internal = workload.spec.firewallConfig?.internal;

  const inputEx = input.firewallConfig.external;
  const inputInt = input.firewallConfig.internal;

  const publicCidr = "0.0.0.0/0";

  inputEx.inbound.public = external?.inboundAllowCIDR?.includes(publicCidr) || false;

  inputEx.inbound.allowCidr = (external?.inboundAllowCIDR || [])
    .filter((cidr) => cidr !== publicCidr)
    .map((cidr) => ({ id: uuidv4(), value: cidr }));

  inputEx.inbound.blockedCidr = (external?.inboundBlockedCIDR || []).map((cidr) => ({ id: uuidv4(), value: cidr }));

  inputEx.outbound.public = external?.outboundAllowCIDR?.includes(publicCidr) || false;

  inputEx.outbound.allowCidr = (external?.outboundAllowCIDR || [])
    .filter((cidr) => cidr !== publicCidr)
    .map((cidr) => ({ id: uuidv4(), value: cidr }));

  inputEx.outbound.blockedCidr = (external?.outboundBlockedCIDR || []).map((cidr) => ({ id: uuidv4(), value: cidr }));

  inputEx.outbound.allowHostname = (external?.outboundAllowHostname || []).map((v) => ({ id: uuidv4(), value: v }));

  inputEx.outbound.allowPort = (external?.outboundAllowPort || []).map((p) => ({
    id: uuidv4(),
    protocol: p.protocol,
    number: String(p.number),
  }));

  inputEx.httpInboundHeaderFilter = (external?.http?.inboundHeaderFilter || []).map((f) => ({
    id: uuidv4(),
    type: f.blockedValues && f.blockedValues.length > 0 ? "blocked" : "allowed",
    key: f.key,
    values: f.blockedValues && f.blockedValues.length > 0 ? f.blockedValues : f.allowedValues || [],
  }));

  if (
    internal?.inboundAllowType === "same-gvc" &&
    internal.inboundAllowWorkload &&
    internal.inboundAllowWorkload.length > 0
  ) {
    inputInt.type = "same-gvc-and-workload-list";
  } else {
    inputInt.type = internal?.inboundAllowType || "none";
  }

  inputInt.allowAccessToItself = (internal?.inboundAllowWorkload || []).includes(selfLink);

  const kedaWorkload = `cpln://internal/keda`;
  inputInt.allowKedaAccess = (internal?.inboundAllowWorkload || []).includes(kedaWorkload);

  const remainingLinks = (internal?.inboundAllowWorkload || []).filter((w) => {
    if (w === selfLink) return false;
    if (w === kedaWorkload) return false;
    return true;
  });

  inputInt.workloads = remainingLinks
    .filter((w) => {
      const parsed = parseLink(w, { useInputCtx: true });
      return parsed.kind !== "agent";
    })
    .map((w) => {
      const parsedWorkload = parseLink(w, { useInputCtx: true });
      return {
        value: parsedWorkload.gvcRelative,
        name: parsedWorkload.name,
        link: parsedWorkload.absolute,
      };
    });

  inputInt.agents = remainingLinks
    .filter((w) => {
      const parsed = parseLink(w, { useInputCtx: true });
      return parsed.kind === "agent";
    })
    .map((w) => {
      const parsedAgent = parseLink(w, { useInputCtx: true });
      return {
        value: parsedAgent.name,
        link: parsedAgent.absolute,
      };
    });

  const sidecar = sidecarToInput(workload.spec.sidecar);
  if (sidecar) {
    input.sidecar = sidecar;
  }

  return input;
}
